/**
 * L&A Wishlist
 * Stores product handles in localStorage and syncs header + product card UI.
 */
(function () {
  const STORAGE_KEY = 'la-wishlist';
  const CHANGE_EVENT = 'wishlist:change';

  const WishlistStore = {
    get() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (error) {
        return [];
      }
    },

    set(handles) {
      const unique = [...new Set(handles.filter(Boolean))];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
      document.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: { handles: unique, count: unique.length },
        })
      );
      return unique;
    },

    has(handle) {
      return this.get().includes(handle);
    },

    add(handle) {
      const handles = this.get();
      if (!handles.includes(handle)) {
        handles.push(handle);
        this.set(handles);
      }
      return true;
    },

    remove(handle) {
      this.set(this.get().filter((item) => item !== handle));
      return false;
    },

    toggle(handle) {
      return this.has(handle) ? this.remove(handle) : this.add(handle);
    },

    count() {
      return this.get().length;
    },
  };

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = (window.Shopify && window.Shopify.locale) || undefined;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getStrings() {
    return (
      (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.wishlist) || {
        add: 'Add to wishlist',
        remove: 'Remove from wishlist',
        empty_title: 'Your wishlist is empty',
        empty_text: 'Save your favourite pieces and find them here later.',
        continue_shopping: 'Continue shopping',
        clear_all: 'Clear wishlist',
        loading: 'Loading your wishlist…',
        error: 'Some products could not be loaded.',
        view_wishlist: 'Wishlist',
      }
    );
  }

  function syncToggleButton(button, isActive) {
    const strings = getStrings();
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', String(isActive));
    button.setAttribute('aria-label', isActive ? strings.remove : strings.add);

    const emptyIcon = button.querySelector('.wishlist-button__icon--empty');
    const filledIcon = button.querySelector('.wishlist-button__icon--filled');

    if (emptyIcon) emptyIcon.hidden = isActive;
    if (filledIcon) filledIcon.hidden = !isActive;
  }

  function syncAllToggleButtons(handles) {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const handle = button.getAttribute('data-product-handle');
      if (!handle) return;
      syncToggleButton(button, handles.includes(handle));
    });
  }

  function syncHeaderIcons(count) {
    const strings = getStrings();
    document.querySelectorAll('[data-wishlist-header]').forEach((link) => {
      const isActive = count > 0;
      link.classList.toggle('is-active', isActive);
      link.setAttribute('aria-label', strings.view_wishlist);
    });
  }

  function refreshUi(handles = WishlistStore.get()) {
    syncAllToggleButtons(handles);
    syncHeaderIcons(handles.length);
  }

  function onToggleClick(event) {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = button.getAttribute('data-product-handle');
    if (!handle) return;

    WishlistStore.toggle(handle);
  }

  async function fetchProduct(handle) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) throw new Error(`Failed to load ${handle}`);
    return response.json();
  }

  function buildWishlistCard(product) {
    const strings = getStrings();
    const image =
      product.featured_image ||
      (product.images && product.images[0]) ||
      '';
    const url = product.url || `/products/${product.handle}`;
    const priceHtml = formatMoney(product.price);
    const compareAt =
      product.compare_at_price && product.compare_at_price > product.price
        ? `<s class="text-subdued">${formatMoney(product.compare_at_price)}</s>`
        : '';
    const title = escapeHtml(product.title);
    const handle = escapeHtml(product.handle);
    const emptyIconHtml =
      document.querySelector('.wishlist-icon-template--empty')?.innerHTML ||
      '<svg aria-hidden="true" focusable="false" fill="none" width="24" class="wishlist-button__svg icon icon-picto-love" viewBox="0 0 24 24"><path clip-rule="evenodd" d="m12 21.844-9.588-10a5.672 5.672 0 0 1-1.063-6.551v0a5.673 5.673 0 0 1 9.085-1.474L12 5.384l1.566-1.565a5.673 5.673 0 0 1 9.085 1.474v0a5.673 5.673 0 0 1-1.062 6.548L12 21.844Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
    const filledIconHtml =
      document.querySelector('.wishlist-icon-template--filled')?.innerHTML ||
      '<svg aria-hidden="true" focusable="false" fill="currentColor" width="24" class="wishlist-button__svg icon icon-picto-love-filled" viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="m12 21.844-9.588-10a5.672 5.672 0 0 1-1.063-6.551v0a5.673 5.673 0 0 1 9.085-1.474L12 5.384l1.566-1.565a5.673 5.673 0 0 1 9.085 1.474v0a5.673 5.673 0 0 1-1.062 6.548L12 21.844Z" fill="currentColor" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

    const article = document.createElement('article');
    article.className = 'product-card wishlist-section__item';
    article.setAttribute('data-wishlist-item', product.handle);

    article.innerHTML = `
      <div class="product-card__figure">
        <button
          type="button"
          class="wishlist-button product-card__wishlist is-active"
          data-wishlist-toggle
          data-product-handle="${handle}"
          data-product-id="${product.id}"
          aria-label="${escapeHtml(strings.remove)}"
          aria-pressed="true"
        >
          <span class="wishlist-button__icon wishlist-button__icon--empty" aria-hidden="true" hidden>
            ${emptyIconHtml}
          </span>
          <span class="wishlist-button__icon wishlist-button__icon--filled" aria-hidden="true">
            ${filledIconHtml}
          </span>
        </button>
        <a href="${escapeHtml(url)}" class="product-card__media" data-instant>
          ${
            image
              ? `<img class="product-card__image product-card__image--primary object-cover aspect-portrait" src="${escapeHtml(image)}" alt="${title}" loading="lazy" width="600" height="750">`
              : `<div class="product-card__image product-card__image--primary aspect-portrait" style="background: rgb(var(--text-color) / 0.06);"></div>`
          }
        </a>
      </div>
      <div class="product-card__info">
        <div class="v-stack gap-2">
          <a href="${escapeHtml(url)}" class="product-card__title">${title}</a>
          <div class="price-list">
            <span class="text-subdued">${priceHtml}</span>
            ${compareAt}
          </div>
        </div>
      </div>
    `;

    return article;
  }

  async function renderWishlistSection(section) {
    const strings = getStrings();
    const grid = section.querySelector('[data-wishlist-grid]');
    const empty = section.querySelector('[data-wishlist-empty]');
    const status = section.querySelector('[data-wishlist-status]');
    const actions = section.querySelector('[data-wishlist-actions]');
    const clearButton = section.querySelector('[data-wishlist-clear]');

    if (!grid || !empty) return;

    const handles = WishlistStore.get();

    grid.innerHTML = '';
    grid.classList.remove('is-visible');
    empty.classList.remove('is-visible');
    if (actions) actions.hidden = true;

    if (status) {
      status.hidden = false;
      status.textContent = strings.loading;
    }

    if (handles.length === 0) {
      if (status) status.hidden = true;
      empty.classList.add('is-visible');
      return;
    }

    const results = await Promise.allSettled(handles.map((handle) => fetchProduct(handle)));
    const products = [];
    const missing = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        products.push(result.value);
      } else {
        missing.push(handles[index]);
      }
    });

    // Drop unavailable handles from storage
    if (missing.length) {
      WishlistStore.set(handles.filter((handle) => !missing.includes(handle)));
    }

    if (status) {
      if (missing.length && products.length) {
        status.hidden = false;
        status.textContent = strings.error;
      } else {
        status.hidden = true;
      }
    }

    if (products.length === 0) {
      empty.classList.add('is-visible');
      return;
    }

    products.forEach((product) => {
      grid.appendChild(buildWishlistCard(product));
    });

    grid.classList.add('is-visible');
    if (actions) actions.hidden = false;

    if (clearButton) {
      clearButton.onclick = () => {
        WishlistStore.set([]);
      };
    }
  }

  function initWishlistSections() {
    document.querySelectorAll('[data-wishlist-section]').forEach((section) => {
      renderWishlistSection(section);
    });
  }

  function init() {
    refreshUi();

    document.addEventListener('click', onToggleClick);

    document.addEventListener(CHANGE_EVENT, (event) => {
      const handles = event.detail?.handles || WishlistStore.get();
      refreshUi(handles);
      initWishlistSections();
    });

    // Re-sync when product lists are dynamically updated
    document.addEventListener('shopify:section:load', () => {
      refreshUi();
      initWishlistSections();
    });

    initWishlistSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LAWishlist = WishlistStore;
})();
